import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface TenantDetail {
  tenant: {
    id: string; name: string; plan: string; status: string; internal: boolean
    country: string | null; locale: string; createdAt: string
    members: { id: string; userId: string; role: string; createdAt: string }[]
    billing: { rail: string; customerRef: string; status: string } | null
    _count: { sites: number; usage: number }
  }
  usageMonthToDate: { action: string; units: number; costCents: number }[]
}

export function AdminTenantDetailPage() {
  const t = useT()
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [planChange, setPlanChange] = useState('starter')

  const tenant = useQuery({ queryKey: ['admin-tenant', id], queryFn: () => apiClient.get<TenantDetail>(`/admin/tenants/${id}`) })

  const action = useMutation({
    mutationFn: (body: { action: string; plan?: string; reason?: string }) =>
      apiClient.post(`/admin/tenants/${id}/actions`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenant', id] }),
  })

  if (tenant.isLoading) return <p>{t('common.loading')}</p>
  if (tenant.isError || !tenant.data) return <p>{t('common.error')}</p>

  const { tenant: row, usageMonthToDate } = tenant.data

  return (
    <div>
      <Link to=".." className="text-xs opacity-70 hover:underline">← {t('admin.tenant.back')}</Link>
      <h1 className="text-2xl font-semibold mb-1 mt-1">{row.name}</h1>
      <p className="text-xs opacity-50 font-mono mb-6">{row.id}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Field label={t('admin.tenant.plan')} value={row.plan} />
        <Field label={t('admin.tenant.status')} value={row.status} />
        <Field label={t('admin.tenant.internal')} value={row.internal ? '✓' : '—'} />
        <Field label={t('admin.tenant.country')} value={row.country ?? '—'} />
        <Field label={t('admin.tenant.locale')} value={row.locale} />
        <Field label={t('admin.tenant.created')} value={new Date(row.createdAt).toLocaleString()} />
      </div>

      <section className="mb-6">
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.tenant.usage_mtd')}</h2>
        {usageMonthToDate.length === 0 ? (
          <p className="text-sm opacity-70">{t('admin.tenant.no_usage')}</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left opacity-70"><th className="py-1 pr-4">action</th><th className="py-1 pr-4">units</th><th className="py-1 pr-4">cost (cents)</th></tr>
            </thead>
            <tbody>
              {usageMonthToDate.map((u) => (
                <tr key={u.action} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="py-1 pr-4">{u.action}</td>
                  <td className="py-1 pr-4">{u.units.toLocaleString()}</td>
                  <td className="py-1 pr-4">{u.costCents.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.tenant.members')}</h2>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left opacity-70"><th className="py-1 pr-4">user</th><th className="py-1 pr-4">role</th><th className="py-1 pr-4">joined</th></tr></thead>
          <tbody>
            {row.members.map((m) => (
              <tr key={m.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-1 pr-4 font-mono text-xs">
                  <Link to={`/admin/users/${m.userId}`} className="hover:underline">{m.userId.slice(0, 8)}…</Link>
                </td>
                <td className="py-1 pr-4">{m.role}</td>
                <td className="py-1 pr-4">{new Date(m.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.tenant.actions')}</h2>
        <div className="p-3 rounded border space-y-2" style={{ borderColor: 'var(--color-border)' }}>
          <label className="block text-xs">
            <span className="opacity-70">{t('admin.tenant.reason')}</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="auth-input mt-1"
            />
          </label>
          <div className="flex gap-2 flex-wrap">
            {row.status === 'active' && (
              <ActionButton onClick={() => action.mutate({ action: 'suspend', reason })}>{t('admin.tenant.act.suspend')}</ActionButton>
            )}
            {(row.status === 'past_due' || row.status === 'quarantined') && (
              <ActionButton onClick={() => action.mutate({ action: 'restore', reason })}>{t('admin.tenant.act.restore')}</ActionButton>
            )}
            {row.status !== 'quarantined' && (
              <ActionButton danger onClick={() => action.mutate({ action: 'quarantine', reason })}>{t('admin.tenant.act.quarantine')}</ActionButton>
            )}
            {!row.internal && (
              <ActionButton onClick={() => action.mutate({ action: 'mark-internal', reason })}>{t('admin.tenant.act.mark_internal')}</ActionButton>
            )}
            {row.internal && (
              <ActionButton onClick={() => action.mutate({ action: 'unmark-internal', reason })}>{t('admin.tenant.act.unmark_internal')}</ActionButton>
            )}
            <ActionButton danger onClick={() => confirm(t('admin.tenant.act.delete_confirm')) && action.mutate({ action: 'soft-delete', reason })}>
              {t('admin.tenant.act.delete')}
            </ActionButton>
          </div>
          <div className="flex gap-2 items-center mt-2">
            <select value={planChange} onChange={(e) => setPlanChange(e.target.value)} className="px-2 py-1 text-sm border rounded bg-transparent" style={{ borderColor: 'var(--color-border)' }}>
              <option value="starter">starter</option>
              <option value="pro">pro</option>
              <option value="business">business</option>
              <option value="enterprise">enterprise</option>
            </select>
            <ActionButton onClick={() => action.mutate({ action: 'change-plan', plan: planChange, reason })}>
              {t('admin.tenant.act.change_plan')}
            </ActionButton>
          </div>
        </div>
      </section>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded border" style={{ borderColor: 'var(--color-border)' }}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-sm mt-0.5">{value}</div>
    </div>
  )
}

function ActionButton({ children, onClick, danger }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-1 text-xs rounded border"
      style={{ borderColor: danger ? 'var(--color-critical)' : 'var(--color-border)', color: danger ? 'var(--color-critical)' : 'inherit' }}
    >
      {children}
    </button>
  )
}
